import cardTemplate from '@/html/card-template.html';

interface V1Config {
  containerId: string;
}

export default class V1 {
  private readonly codename = 'stratos';
  private config: V1Config = {
    containerId: '',
  };
  private iframe: HTMLIFrameElement | null = null;
  private postUrl?: string;

  constructor(config: V1Config) {
    if (!config.containerId) throw new Error('container id is required');
    this.config = { ...this.config, ...config };
  }

  async init() {
    let container = document.getElementById(this.config.containerId);
    if (!container) throw new Error('container not found');

    this.iframe = document.createElement('iframe');
    this.iframe.style.width = '100%';
    this.iframe.style.height = '100%';
    this.iframe.style.border = 'none';

    container.appendChild(this.iframe);

    try {
      // Get the iframe document to inject content
      const iframeDocument =
        this.iframe.contentDocument || this.iframe.contentWindow?.document;
      if (iframeDocument) {
        iframeDocument.open();

        // Append the HTML content directly into the iframe
        iframeDocument.write(cardTemplate);
        iframeDocument.close();
      }
    } catch (error) {
      console.log(error);
      if (error instanceof Error) {
        throw new Error(error.message);
      } else {
        throw new Error(String(error));
      }
    }
  }

  public validateCard() {
    return new Promise((resolve, reject) => {
      const handleMessage = (event: MessageEvent) => {
        if (event.source === this.iframe?.contentWindow) {
          window.removeEventListener('message', handleMessage);

          if (
            event.data?.code === 'cc-form-validation' &&
            event.data?.status === 'error'
          )
            return reject({
              code: event.data?.code,
              message: 'Form validation failed',
            });

          resolve(true);
        }
      };

      window.addEventListener('message', handleMessage);
      this.iframe?.contentWindow?.postMessage('VALIDATE_CARD_FORMDATA', '*');
    });
  }

  public async submit(id: string) {
    if (!id)
      throw {
        code: 'id-required',
        message: 'ID is required',
      };

    const result = await this.getPaymentUrl(id).catch((e) => {
      throw {
        code: 'get-payment-url-error',
        message: String(e),
      };
    });
    const resultJson = await result.json();

    if ([10026, 10273].includes(resultJson?.response_code)) {
      throw {
        code: 'invalid-id',
        message: 'ID does not exist',
      };
    } else if (resultJson?.response_code !== 2100) {
      throw {
        code: 'api-response-error',
        message: (resultJson?.description as string)
          .toLowerCase()
          .replace(/_/g, ' '),
      };
    } else if (!resultJson?.data?.direct_post_url) {
      throw {
        code: 'missing-url',
        message: 'Missing url. Please contact Leanx Support.',
      };
    }

    this.postUrl = resultJson?.data?.direct_post_url;

    return new Promise((resolve, reject) => {
      const handleMessage = async (event: MessageEvent) => {
        if (event.source === this.iframe?.contentWindow) {
          window.removeEventListener('message', handleMessage);
          if (
            event.data?.code === 'cc-form-validation' &&
            event.data?.status === 'error'
          ) {
            return reject({
              code: event.data?.code,
              message: 'Form validation failed',
            });
          }

          this.postPaymentData(JSON.parse(event.data?.data));
          resolve(true);
        }
      };

      window.addEventListener('message', handleMessage);
      this.iframe?.contentWindow?.postMessage('GET_CARD_FORMDATA', '*');
    });
  }

  private async getPaymentUrl(id: string) {
    const endpoint =
      (process.env.SWITCH_API_URL as string) +
      (process.env.SWITCH_API_VERSION as string) +
      (process.env.SWITCH_API_ENV as string) +
      (process.env.ENDPOINT_CARD_INFO as string);

    return fetch(endpoint + `?invoice_no=${id}`);
  }

  private async postPaymentData(data: {
    cardholder_name: string;
    card_number: string;
    expires: string;
    cvc: string;
  }) {
    const f = document.createElement('form');
    f.method = 'POST';
    f.action = this.postUrl as string;

    const nameInput = document.createElement('input');
    nameInput.type = 'hidden';
    nameInput.name = 'cardholder_name';
    nameInput.value = data.cardholder_name;
    f.appendChild(nameInput);

    const numberInput = document.createElement('input');
    numberInput.type = 'hidden';
    numberInput.name = 'card_number';
    numberInput.value = data.card_number;
    f.appendChild(numberInput);

    const expiryInput = document.createElement('input');
    expiryInput.type = 'hidden';
    expiryInput.name = 'expires';
    expiryInput.value = data.expires;
    f.appendChild(expiryInput);

    const cvvInput = document.createElement('input');
    cvvInput.type = 'hidden';
    cvvInput.name = 'cvc';
    cvvInput.value = data.cvc;
    f.appendChild(cvvInput);

    document.body.appendChild(f);

    try {
      f.submit();
    } catch (error) {
      throw {
        code: 'post-form-payment-error',
        message: 'Error submitting payment form',
      };
    }
  }
}
